import React, {useEffect, useRef, useState} from 'react'
import {useMutation, useQueryClient} from 'react-query'
import {QUERIES, KTIcon} from '../../../../../_metronic/helpers'
import {
  importClassifiedPosts,
  getAllClassifiedCategories,
  getClassifiedSubCategoriesByCategory,
} from '../../core/_requests'
import {useListFailedView} from '../../core/ListFailedProvider'
import {ClassifiedCategory, ClassifiedSubCategory} from '../../core/_models'

export const UploadBulkData = () => {
  const queryClient = useQueryClient()
  const {setFailedList} = useListFailedView()
  const [errorMessage, setErrMessage] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('')
  const [categories, setCategories] = useState<ClassifiedCategory[]>([])
  const [subCategories, setSubCategories] = useState<ClassifiedSubCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSubCategories, setLoadingSubCategories] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true)
      try {
        const data = await getAllClassifiedCategories()
        setCategories(data)
      } catch (error) {
        console.error('Failed to load categories', error)
      } finally {
        setLoadingCategories(false)
      }
    }
    loadCategories()
  }, [])

  // Load subcategories when category changes
  useEffect(() => {
    if (selectedCategory) {
      const loadSubCategories = async () => {
        setLoadingSubCategories(true)
        try {
          const data = await getClassifiedSubCategoriesByCategory(selectedCategory)
          setSubCategories(data)
          setSelectedSubCategory('') // Reset subcategory when category changes
        } catch (error) {
          console.error('Failed to load subcategories', error)
          setSubCategories([])
        } finally {
          setLoadingSubCategories(false)
        }
      }
      loadSubCategories()
    } else {
      setSubCategories([])
      setSelectedSubCategory('')
    }
  }, [selectedCategory])

  const uploadFile = useMutation((formData: FormData) => importClassifiedPosts(formData), {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey?.[0] === 'string' &&
          query.queryKey[0].toString().startsWith(QUERIES.CLASSIFIED_POSTS_LIST),
      })
    },
  })

  useEffect(() => {
    if (uploadFile.isSuccess && uploadFile.data) {
      setFailedList(uploadFile.data)
    }
  }, [uploadFile.isSuccess, uploadFile.data, setFailedList])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setErrMessage('')

    if (file) {
      // Validate category and subcategory are selected
      if (!selectedCategory || !selectedSubCategory) {
        setErrMessage('Please select both Category and SubCategory before uploading')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      const isExcelFile = /\.(xls|xlsx)$/i.test(file.name)
      if (isExcelFile) {
        const formData = new FormData()
        formData.append('excelFile', file)
        formData.append('category', String(selectedCategory))
        formData.append('subCategory', String(selectedSubCategory))
        uploadFile.mutate(formData)
      } else {
        setErrMessage('File format must be excel (.xls, .xlsx)')
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className='w-100'>
      {/* Category and SubCategory Selection Row */}
      <div className='d-flex flex-wrap align-items-end gap-4 mb-4'>
        {/* Category Selection */}
        <div className='flex-grow-1' style={{minWidth: '200px'}}>
          <label className='form-label fw-bold fs-6 mb-2'>
            Category <span className='text-danger'>*</span>
          </label>
          <select
            className='form-select form-select-solid'
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            disabled={loadingCategories || uploadFile.isLoading}
          >
            <option value=''>Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={String(cat.id ?? '')}>
                {cat.name}
              </option>
            ))}
          </select>
          {loadingCategories && (
            <div className='text-muted fs-7 mt-1'>
              <span className='spinner-border spinner-border-sm me-2'></span>
              Loading categories...
            </div>
          )}
        </div>

        {/* SubCategory Selection */}
        <div className='flex-grow-1' style={{minWidth: '200px'}}>
          <label className='form-label fw-bold fs-6 mb-2'>
            SubCategory <span className='text-danger'>*</span>
          </label>
          <select
            className='form-select form-select-solid'
            value={selectedSubCategory}
            onChange={(e) => setSelectedSubCategory(e.target.value)}
            disabled={!selectedCategory || loadingSubCategories || uploadFile.isLoading}
          >
            <option value=''>
              {!selectedCategory ? 'Select Category first' : 'Select SubCategory'}
            </option>
            {subCategories.map((subCat) => (
              <option key={subCat.id} value={String(subCat.id ?? '')}>
                {subCat.name}
              </option>
            ))}
          </select>
          {loadingSubCategories && (
            <div className='text-muted fs-7 mt-1'>
              <span className='spinner-border spinner-border-sm me-2'></span>
              Loading subcategories...
            </div>
          )}
        </div>

        {/* Upload File Button */}
        <div>
          <label className='form-label fw-bold fs-6 mb-2 d-block'>&nbsp;</label>
          <input
            type='file'
            accept='.xlsx, .xls'
            onChange={handleFileChange}
            style={{display: 'none'}}
            ref={fileInputRef}
          />
          <button
            type='button'
            id='kt_sign_in_submit'
            onClick={handleUploadButtonClick}
            className='btn btn-primary d-flex align-items-center'
            disabled={uploadFile.isLoading || !selectedCategory || !selectedSubCategory}
            style={{minWidth: '140px'}}
          >
            {!uploadFile.isLoading ? (
              <>
                <KTIcon iconName='exit-up' className='fs-2 me-2' />
                <span className='indicator-label'>Upload File</span>
              </>
            ) : (
              <span className='indicator-progress d-flex align-items-center'>
                <span className='spinner-border spinner-border-sm me-2'></span>
                Uploading...
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Error Messages */}
      {errorMessage && (
        <div className='alert alert-danger d-flex align-items-center p-3 mb-4'>
          <KTIcon iconName='information' className='fs-2 me-2' />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Success Message */}
      {uploadFile.isSuccess && (
        <div className='alert alert-success d-flex align-items-center p-3 mb-4'>
          <KTIcon iconName='check-circle' className='fs-2 me-2' />
          <span>File uploaded successfully. Records will be updated soon!</span>
        </div>
      )}

      {/* Error Message */}
      {uploadFile.isError && (
        <div className='alert alert-danger d-flex align-items-center p-3 mb-4'>
          <KTIcon iconName='cross-circle' className='fs-2 me-2' />
          <span>Oops! Unable to upload file. Please try again.</span>
        </div>
      )}
    </div>
  )
}
